import React from 'react';
import { IconTrophy, IconCrown, IconEdit } from '@tabler/icons-react';

const BracketView = ({ matches = [], format = 'single-elimination', isAdmin = false, onMatchClick }) => {
  // Calculate total rounds needed based on number of matches or participants
  const calculateTotalRounds = () => {
    if (matches.length === 0) return 0;

    // Find the highest round number in existing matches
    const maxExistingRound = Math.max(...matches.map(m => m.round || 1));

    // For single elimination, we need rounds until we get to 1 winner
    // Find first round to get participant count
    const firstRoundMatches = matches.filter(m => m.round === 1);
    if (firstRoundMatches.length === 0) return maxExistingRound;

    const totalParticipants = firstRoundMatches.length * 2;
    const totalRoundsNeeded = Math.ceil(Math.log2(totalParticipants));

    return Math.max(totalRoundsNeeded, maxExistingRound);
  };

  const totalRounds = calculateTotalRounds();

  // Organize matches by round - only display matches that actually exist
  const matchesByRound = {};

  // Add existing matches only
  matches.forEach(match => {
    const round = match.round || 1;
    if (!matchesByRound[round]) {
      matchesByRound[round] = [];
    }
    matchesByRound[round].push(match);
  });

  const rounds = Object.keys(matchesByRound).sort((a, b) => Number(a) - Number(b));
  const maxRound = totalRounds;

  const getRoundLabel = (roundNum) => {
    const round = Number(roundNum);
    if (round === maxRound) return 'Finals';
    if (round === maxRound - 1) return 'Semi-Finals';
    if (round === maxRound - 2) return 'Quarter-Finals';
    return `Round ${round}`;
  };

  const getMatchStatusClass = (match) => {
    if (match.status === 'completed') return 'completed';
    if (match.status === 'in_progress') return 'in-progress';
    return 'upcoming';
  };

  const handleMatchClick = (match) => {
    if (isAdmin && onMatchClick && !match.isTBD) {
      onMatchClick(match);
    }
  };

  const renderMatch = (match) => {
    const isCompleted = match.status === 'completed';
    const isTBD = match.isTBD || false;
    const participant1Won = isCompleted && match.winnerId === match.participant1Id;
    const participant2Won = isCompleted && match.winnerId === match.participant2Id;
    const canEdit = isAdmin && !isTBD && onMatchClick;

    return (
      <div
        key={match.id}
        className={`ls-bracket-match ${getMatchStatusClass(match)} ${isTBD ? 'ls-tbd-match' : ''} ${canEdit ? 'ls-editable' : ''}`}
        onClick={() => handleMatchClick(match)}
        role={canEdit ? 'button' : undefined}
        tabIndex={canEdit ? 0 : undefined}
        onKeyDown={(e) => {
          if (canEdit && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            handleMatchClick(match);
          }
        }}
      >
        {canEdit && (
          <div className="ls-match-edit-overlay">
            <IconEdit size={20} />
            <span>Edit Score</span>
          </div>
        )}

        <div className="ls-match-participants">
          <div className={`ls-participant ${participant1Won ? 'ls-winner' : ''} ${participant2Won ? 'ls-loser' : ''} ${isTBD ? 'ls-tbd' : ''}`}>
            <div className="ls-participant-name">
              {participant1Won && <IconCrown size={14} className="ls-winner-icon" />}
              <span>{match.participant1Name || 'TBD'}</span>
            </div>
            <div className="ls-participant-score">
              {isCompleted && !isTBD ? match.participant1Score || 0 : '-'}
            </div>
          </div>

          <div className={`ls-participant ${participant2Won ? 'ls-winner' : ''} ${participant1Won ? 'ls-loser' : ''} ${isTBD ? 'ls-tbd' : ''}`}>
            <div className="ls-participant-name">
              {participant2Won && <IconCrown size={14} className="ls-winner-icon" />}
              <span>{match.participant2Name || 'TBD'}</span>
            </div>
            <div className="ls-participant-score">
              {isCompleted && !isTBD ? match.participant2Score || 0 : '-'}
            </div>
          </div>
        </div>

        {!isTBD && match.status === 'in_progress' && (
          <div className="ls-match-status-badge ls-in-progress">LIVE</div>
        )}
      </div>
    );
  };

  if (matches.length === 0) {
    return (
      <div className="ls-bracket-empty">
        <IconTrophy size={48} className="ls-bracket-empty-icon" stroke={1.5} />
        <p>No bracket matches yet</p>
        <span className="ls-empty-hint">Create matches to see the tournament bracket</span>
      </div>
    );
  }

  return (
    <div className="ls-bracket-view-container">
      <div className="ls-bracket-scroll-wrapper">
        <div className="ls-bracket-grid">
          {rounds.map((roundNum) => {
            const roundNumber = Number(roundNum);
            const matchesInRound = matchesByRound[roundNum];

            return (
              <div key={roundNum} className={`ls-bracket-round ls-round-${roundNumber}`} data-round={roundNumber}>
                <div className="ls-round-label">
                  {getRoundLabel(roundNum)}
                </div>
                <div className="ls-round-matches-container">
                  {matchesInRound.map((match, index) => (
                    <div
                      key={match.id}
                      className="ls-match-wrapper"
                      data-match-index={index}
                    >
                      {renderMatch(match)}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {format === 'double-elimination' && (
        <div className="ls-bracket-note">
          <span className="ls-note-icon">ℹ️</span>
          <span>Double Elimination: Losers bracket coming soon</span>
        </div>
      )}
    </div>
  );
};

export default BracketView;
